import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Button,
  WidgetEmptyErrorPlaceholder,
} from "@ann/web-components";
import { useToast } from "../../../../../../../../../components/toast";
import { useStore } from "../../../../../../../../../hooks";
import { useSubDepartments } from "../../../../../../context";
import {
  SubDepartmentsContainer,
  CardHeader,
  HeaderLeft,
  HeaderRight,
  HeaderTitle,
  HeaderDescription,
  CountText,
  CardContent,
  SubDepartmentsList,
  EmptyStateContainer,
  EmptyStateWrapper,
} from "./SubDepartments.styled";
import CreateSubDepartmentModal from "./CreateSubDepartmentModal";
import SubDepartmentCard from "./SubDepartmentCard";

const SubDepartments = ({
  departmentName = "Sales & Business Development",
  onAddSubDepartment,
}) => {
  const { departmentsStore } = useStore();
  const {
    subDepartments,
    loading,
    creating,
    error,
    fetchSubDepartments,
    createSubDepartment,
  } = useSubDepartments();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const toast = useToast();

  const department = departmentsStore.currentDepartment;
  const parentDepartmentUuid =
    department?.department_uuid ||
    department?.department_id ||
    department?.id;

  useEffect(() => {
    if (parentDepartmentUuid) {
      fetchSubDepartments(parentDepartmentUuid);
    }
  }, [parentDepartmentUuid, fetchSubDepartments]);

  const hasSubDepartments = subDepartments.length > 0;
  const count = subDepartments.length;
  const existingSubDepartmentNames = subDepartments
    .map((subDept) => subDept.departmentName || subDept.department_name || subDept.name || "")
    .filter(Boolean);

  const handleOpenModal = () => {
    setIsCreateModalOpen(true);
    if (onAddSubDepartment) {
      onAddSubDepartment();
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateSubDepartment = async (subDepartmentData) => {
    if (!parentDepartmentUuid) {
      toast.error({
        title: "Error",
        message: "Parent department UUID is required",
      });
      return;
    }

    try {
      const createdSubDepartment = await createSubDepartment(
        parentDepartmentUuid,
        {
          name: subDepartmentData.name,
          description: subDepartmentData.description,
          owner_uuid: subDepartmentData.adminOwner?.value,
        }
      );

      if (createdSubDepartment) {
        await fetchSubDepartments(parentDepartmentUuid);
        handleCloseModal();
        toast.success({
          title: "Sub-department created successfully",
          message: `${subDepartmentData.name} has been created`,
        });
      }
    } catch (error) {
      toast.error({
        title: "Error",
        message: error.message || "Failed to create sub-department",
      });
    }
  };

  return (
    <SubDepartmentsContainer>
      <Card size="medium">
        <CardHeader>
          <HeaderLeft>
            <HeaderTitle>
              <Typography category="subtitle" size="small" weight="bold">
                Sub-Departments
              </Typography>
            </HeaderTitle>
            <HeaderDescription>
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="body"
              >
                Regional and specialized departments under {departmentName}.
              </Typography>
            </HeaderDescription>
          </HeaderLeft>
          <HeaderRight>
            <CountText>
              <Typography
                category="body"
                size="medium"
                weight="regular"
                color="body"
              >
                {count} sub-department{count !== 1 ? "s" : ""}
              </Typography>
            </CountText>
            <Button
              label="Add Sub-Department"
              size="medium"
              type="primary"
              iconLeft="plus"
              onClick={handleOpenModal}
            />
          </HeaderRight>
        </CardHeader>
        <CardContent>
          {loading ? (
            <EmptyStateContainer>
              <EmptyStateWrapper>
                <Typography category="body" size="medium" weight="regular" color="body">
                  Loading sub-departments...
                </Typography>
              </EmptyStateWrapper>
            </EmptyStateContainer>
          ) : error ? (
            <EmptyStateContainer>
              <EmptyStateWrapper>
                <WidgetEmptyErrorPlaceholder
                  icon="error"
                  title="Error loading sub-departments"
                  message={error.message || "Failed to load sub-departments"}
                  showButton={false}
                />
              </EmptyStateWrapper>
            </EmptyStateContainer>
          ) : hasSubDepartments ? (
            <SubDepartmentsList>
              {subDepartments.map((subDept) => (
                <SubDepartmentCard
                  key={
                    subDept.department_uuid ||
                    subDept.sub_department_id ||
                    subDept.id ||
                    subDept.department_id
                  }
                  subDepartment={{
                    ...subDept,
                    departmentName: subDept.departmentName || subDept.department_name || subDept.name,
                    ownerName: subDept.ownerName || subDept.owner_name,
                    updatedAt: subDept.updatedAt || subDept.updated_at,
                    parent_department_name: departmentName,
                  }}
                />
              ))}
            </SubDepartmentsList>
          ) : (
            <EmptyStateContainer>
              <EmptyStateWrapper>
                <WidgetEmptyErrorPlaceholder
                  icon="searchOff"
                  title="No sub-departments"
                  message="This department does not have any sub-departments yet."
                  showButton={false}
                />
              </EmptyStateWrapper>
            </EmptyStateContainer>
          )}
        </CardContent>
      </Card>

      <CreateSubDepartmentModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        onCreateSubDepartment={handleCreateSubDepartment}
        parentDepartmentName={departmentName}
        existingNames={existingSubDepartmentNames}
      />
    </SubDepartmentsContainer>
  );
};

export default SubDepartments;
